import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Trophy, Lock, Check, Flame, Sparkles, Download, Image as ImageIcon, X, Zap, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import RankShareCard from "../components/RankShareCard";
import RankBadge from "../components/RankBadge";
import SharePopover from "../components/SharePopover";
import RankUpModal from "../components/RankUpModal";
import { buildShareText, SHARE_URL } from "../lib/share";

const TIER_ORDER = ["Foundation", "Progression", "Ascension", "Elite", "Apex", "Legend"];

const TIER_META = {
    Foundation: { blurb: "Where everyone starts. The bar doesn't know you yet.", accent: "#94a3b8" },
    Progression: { blurb: "You stopped being a tourist. You're a regular now.", accent: "#f59e0b" },
    Ascension: { blurb: "The weight is heavier. So are you. So is the question 'who's next?'", accent: "#ef4444" },
    Elite: { blurb: "Less than 5% of lifters make it here. You're the question other people ask.", accent: "#a855f7" },
    Apex: { blurb: "Records aren't a goal anymore — they're paperwork.", accent: "#22d3ee" },
    Legend: { blurb: "Iron is just a side effect of who you've become.", accent: "#fde047" },
};

export default function Ranks() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [activeRank, setActiveRank] = useState(null);
    const [me, setMe] = useState(null);
    const [granting, setGranting] = useState(false);
    const [rankUpEvent, setRankUpEvent] = useState(null);
    const cardRef = useRef(null);

    const isAdmin = me?.role === "admin";

    const refresh = () => api.get("/progress/rank").then((r) => setData(r.data));

    const handleGrantExp = async () => {
        if (granting) return;
        setGranting(true);
        try {
            const r = await api.post("/admin/grant-exp", { amount: 1000 });
            await refresh();
            toast.success(`+${r.data.granted.toLocaleString()} EXP granted. New total: ${r.data.total_exp.toLocaleString()}`);
            // If the grant pushed across a rank threshold, surface the same
            // celebration modal users see on a real workout finish.
            if (r.data.rank_up) setRankUpEvent({ rankUp: r.data.rank_up, expEarned: r.data.granted });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Grant failed");
        } finally {
            setGranting(false);
        }
    };

    const handleResetExp = async () => {
        if (granting) return;
        if (!window.confirm("Zero out your EXP? This is an admin test action.")) return;
        setGranting(true);
        try {
            await api.post("/admin/reset-exp");
            await refresh();
            toast.success("EXP reset to 0");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally {
            setGranting(false);
        }
    };

    useEffect(() => {
        api.get("/auth/me").then(r => setMe(r.data)).catch(() => null);
        api.get("/progress/rank")
            .then(r => setData(r.data))
            .catch(e => setError(e?.response?.data?.detail || "Failed to load ranks"));
    }, []);

    const handleDownload = async () => {
        if (!cardRef.current) return;
        setDownloading(true);
        try {
            await new Promise(r => requestAnimationFrame(r));
            const dataUrl = await toPng(cardRef.current, {
                cacheBust: true,
                pixelRatio: 1,
                width: 1080,
                height: 1080,
                backgroundColor: "#0a0a0a",
            });
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `hypergains-rank-${data.current.id}-${data.current.name.toLowerCase().replace(/\s+/g, "-")}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Card saved — share it on your story.");
        } catch (e) {
            toast.error("Could not save the card. Try again.");
        } finally {
            setDownloading(false);
        }
    };

    const grouped = useMemo(() => {
        if (!data) return [];
        const map = {};
        for (const r of data.all_ranks) {
            (map[r.tier] ||= []).push(r);
        }
        return TIER_ORDER.filter(t => map[t]).map(t => ({ tier: t, ranks: map[t] }));
    }, [data]);

    if (error) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-12">
                <div className="tactical-card p-6 text-sm text-muted-foreground" data-testid="ranks-error">{error}</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-12">
                <div className="tactical-card p-6 text-sm text-muted-foreground" data-testid="ranks-loading">Loading ranks…</div>
            </div>
        );
    }

    const currentId = data.current.id;
    const nextId = data.next?.id;

    return (
        <div className="mx-auto max-w-5xl px-4 py-8 sm:py-12" data-testid="ranks-page">
            {/* Off-screen 1080x1080 share card */}
            <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", opacity: 0 }} aria-hidden="true">
                <RankShareCard ref={cardRef} rank={data.current} totalExp={data.total_exp} />
            </div>

            {/* Hero */}
            <div className="relative overflow-hidden border border-border bg-card p-6 sm:p-10">
                <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-accent-red/10 blur-3xl" />
                <div className="absolute -bottom-24 -left-12 h-72 w-72 rounded-full bg-accent-red/5 blur-3xl" />
                <div className="relative">
                    <div className="label-eyebrow text-accent-red flex items-center gap-2">
                        <Flame className="h-4 w-4" /> The Ladder
                    </div>
                    <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[0.95] sm:text-5xl lg:text-6xl">
                        20 Ranks. <span className="text-accent-red">One way up.</span>
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
                        Every set you log moves you. There are no shortcuts, no boosts, no daily bonuses. Just tension, progress, and time. Find out who you are.
                    </p>

                    <div className="mt-5 flex flex-wrap items-center gap-2">
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            data-testid="rank-share-download"
                            className="inline-flex items-center gap-2 rounded-sm border border-accent-red bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-[color:var(--accent-hover)] hover:shadow-[0_0_24px_-4px_rgba(239,68,68,0.7)] disabled:opacity-60"
                        >
                            {downloading ? <ImageIcon className="h-3.5 w-3.5 animate-pulse" /> : <Download className="h-3.5 w-3.5" />}
                            {downloading ? "Saving card…" : "Save share card (1080×1080)"}
                        </button>
                        <SharePopover
                            text={buildShareText(data.current, data.total_exp)}
                            // Personalised landing-page URL: shows recipient a teaser hero with
                            // your rank + insignia + name. Backend localStorage redeems the join.
                            url={`${SHARE_URL}/?invite_rank=${data.current.id}${me?.name ? `&from=${encodeURIComponent(me.name)}` : ""}`}
                            getFile={async () => {
                                if (!cardRef.current) return null;
                                await new Promise(r => requestAnimationFrame(r));
                                const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 1, width: 1080, height: 1080, backgroundColor: "#0a0a0a" });
                                const blob = await (await fetch(dataUrl)).blob();
                                return new File([blob], `hypergains-rank-${data.current.id}-${data.current.name.toLowerCase().replace(/\s+/g, "-")}.png`, { type: "image/png" });
                            }}
                            label="Share rank"
                            testIdPrefix="rank-share"
                        />
                    </div>

                    {/* Admin-only EXP utility — lets the admin walk the entire
                        rank ladder + verify the celebration modal & plate-clang
                        without grinding real workouts. Granting EXP that pushes
                        across a tier threshold triggers the live RankUpModal
                        below (same component users see post-workout). */}
                    {isAdmin && (
                        <div
                            className="mt-4 flex flex-wrap items-center gap-2 rounded-sm border border-dashed border-accent-red/50 bg-accent-red/5 p-3"
                            data-testid="admin-exp-tools"
                        >
                            <span className="label-eyebrow mr-1 text-accent-red">Admin · test tools</span>
                            <button
                                onClick={handleGrantExp}
                                disabled={granting}
                                data-testid="admin-grant-exp"
                                className="inline-flex items-center gap-1.5 rounded-sm bg-accent-red px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-60"
                            >
                                <Zap className="h-3.5 w-3.5" /> +1,000 EXP
                            </button>
                            <button
                                onClick={handleResetExp}
                                disabled={granting}
                                data-testid="admin-reset-exp"
                                className="inline-flex items-center gap-1.5 rounded-sm border border-accent-red/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-foreground hover:bg-accent-red/10 disabled:opacity-60"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset to 0
                            </button>
                            <span className="text-[11px] text-muted-foreground">Crossing a tier triggers the live celebration modal + plate-clang.</span>
                        </div>
                    )}

                    {/* Current status strip */}
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="border border-border bg-background/60 p-4">
                            <div className="label-eyebrow text-muted-foreground">Current Rank</div>
                            <div className="mt-1 font-display text-xl font-black uppercase" data-testid="ranks-current-name">{data.current.name}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{data.current.tier} Tier</div>
                        </div>
                        <div className="border border-border bg-background/60 p-4">
                            <div className="label-eyebrow text-muted-foreground">Total EXP</div>
                            <div className="mt-1 font-display text-xl font-black tabular-nums" data-testid="ranks-total-exp">{data.total_exp.toLocaleString()}</div>
                            <div className="mt-1 text-xs text-muted-foreground">Earned through work, not wishes</div>
                        </div>
                        <div className="border border-border bg-background/60 p-4">
                            <div className="label-eyebrow text-muted-foreground">{data.next ? "Next" : "Status"}</div>
                            <div className="mt-1 font-display text-xl font-black uppercase">
                                {data.next ? data.next.name : "Maxed Out"}
                            </div>
                            <div className="mt-1 text-xs text-accent-red">
                                {data.next ? `${data.to_next.toLocaleString()} EXP to go · ${data.pct}%` : "You are the ceiling."}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tiers */}
            <div className="mt-10 space-y-12">
                {grouped.map(({ tier, ranks }) => {
                    const tierMeta = TIER_META[tier] || { blurb: "", accent: "#ef4444" };
                    const tierUnlocked = ranks.some(r => r.id <= currentId);
                    return (
                        <section key={tier} data-testid={`ranks-tier-${tier.toLowerCase()}`}>
                            {/* Tier header */}
                            <div className="flex items-end justify-between gap-4 border-b border-border pb-3">
                                <div>
                                    <div className="label-eyebrow" style={{ color: tierMeta.accent }}>{tierUnlocked ? "Tier Unlocked" : "Locked Tier"}</div>
                                    <h2 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">{tier}</h2>
                                </div>
                                <p className="hidden max-w-md text-right text-xs italic text-muted-foreground sm:block">"{tierMeta.blurb}"</p>
                            </div>

                            {/* Timeline */}
                            <ol className="relative mt-6 space-y-4 border-l-2 border-border pl-6 sm:pl-10">
                                {ranks.map(r => {
                                    const unlocked = r.id <= currentId;
                                    const isCurrent = r.id === currentId;
                                    const isNext = r.id === nextId;
                                    // Show the tagline for every unlocked rank PLUS the immediate next
                                    // one (a teaser of what's coming). Locked ranks beyond that stay
                                    // masked with "????" until reached.
                                    const taglineVisible = unlocked || isNext;
                                    const clickable = taglineVisible;
                                    return (
                                        <li key={r.id} className="relative" data-testid={`rank-row-${r.id}`}>
                                            {/* Badge node — replaces the previous Lock/Check icon */}
                                            <span className={`absolute -left-[44px] sm:-left-[62px] top-2`}>
                                                <RankBadge rankId={r.id} size="sm" locked={!unlocked} />
                                            </span>

                                            <button
                                                type="button"
                                                onClick={() => clickable && setActiveRank(r)}
                                                disabled={!clickable}
                                                data-testid={`rank-row-button-${r.id}`}
                                                className={`block w-full text-left border bg-card p-4 transition-colors sm:p-5 ${
                                                    isCurrent ? "border-accent-red shadow-[0_0_0_1px_var(--accent-red)]"
                                                        : isNext ? "border-accent-red/40"
                                                        : "border-border"
                                                } ${clickable ? "hover:border-accent-red/70 cursor-pointer" : "cursor-default opacity-90"}`}
                                            >
                                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                                    <div className="flex items-baseline gap-3">
                                                        <span className={`font-display text-xs font-black tabular-nums ${isCurrent ? "text-accent-red" : "text-muted-foreground"}`}>
                                                            #{r.id.toString().padStart(2, "0")}
                                                        </span>
                                                        <h3 className={`font-display text-xl font-black uppercase leading-tight sm:text-2xl ${unlocked ? "text-foreground" : "text-foreground/70"}`}>
                                                            {r.name}
                                                        </h3>
                                                        {isCurrent && (
                                                            <span className="label-eyebrow rounded-sm bg-accent-red px-2 py-0.5 text-white" data-testid="rank-row-current-badge">You</span>
                                                        )}
                                                        {isNext && !isCurrent && (
                                                            <span className="label-eyebrow rounded-sm border border-accent-red px-2 py-0.5 text-accent-red">Next</span>
                                                        )}
                                                        {!unlocked && (
                                                            <span className="label-eyebrow rounded-sm border border-border px-2 py-0.5 text-muted-foreground" data-testid={`rank-row-locked-${r.id}`}>
                                                                <Lock className="mr-0.5 inline h-2.5 w-2.5" /> Locked
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <Trophy className={`h-3.5 w-3.5 ${unlocked ? "text-accent-red" : "text-muted-foreground"}`} />
                                                        <span className="font-display font-black tabular-nums">{r.min_exp.toLocaleString()}</span>
                                                        <span className="label-eyebrow text-muted-foreground">EXP</span>
                                                    </div>
                                                </div>
                                                <p className={`mt-2 text-sm italic ${unlocked ? "text-foreground/80" : "text-muted-foreground"}`} data-testid={`rank-row-tagline-${r.id}`}>
                                                    {taglineVisible ? `"${r.tagline}"` : <span className="tracking-[0.4em]">????</span>}
                                                </p>
                                                {isCurrent && data.next && (
                                                    <div className="mt-3">
                                                        <div className="h-1.5 w-full bg-border">
                                                            <div className="h-full bg-accent-red transition-[width] duration-700" style={{ width: `${data.pct}%` }} />
                                                        </div>
                                                        <div className="mt-1 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                                                            <span>{data.into_current.toLocaleString()} EXP into rank</span>
                                                            <span className="text-accent-red">{data.to_next.toLocaleString()} to {data.next.name}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ol>
                        </section>
                    );
                })}
            </div>

            {/* Footer note */}
            <div className="mt-12 border border-dashed border-border bg-card/40 p-5 text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">EXP is earned by finishing workouts and beating your last numbers.</p>
                <p className="mt-1 text-sm italic text-foreground/70">No streaks. No daily logins. Just lifts.</p>
            </div>

            {/* Rank detail modal */}
            {activeRank && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setActiveRank(null)}
                    data-testid="rank-detail-modal"
                >
                    <div
                        className="relative w-full max-w-md border-2 border-accent-red bg-card p-6 sm:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveRank(null)}
                            data-testid="rank-detail-close"
                            className="absolute right-2 top-2 rounded-sm border border-border bg-secondary/30 p-1.5 hover:bg-secondary"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                        <div className="flex flex-col items-center text-center">
                            <RankBadge rankId={activeRank.id} size="lg" locked={activeRank.id > currentId} />
                            <div className="mt-4 label-eyebrow text-accent-red">{activeRank.tier} Tier · #{activeRank.id.toString().padStart(2, "0")}</div>
                            <h3 className="mt-1 font-display text-3xl font-black uppercase">{activeRank.name}</h3>
                            <p className="mt-3 italic text-foreground/85" data-testid="rank-detail-tagline">"{activeRank.tagline}"</p>
                            {activeRank.id === currentId && data.next && (
                                <div className="mt-5 w-full">
                                    <div className="label-eyebrow text-muted-foreground">Progress to {data.next.name}</div>
                                    <div className="mt-2 h-2 w-full bg-border">
                                        <div className="h-full bg-accent-red" style={{ width: `${data.pct}%` }} />
                                    </div>
                                    <div className="mt-1 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                                        <span>{data.into_current.toLocaleString()} into rank</span>
                                        <span className="text-accent-red">{data.to_next.toLocaleString()} to {data.next.name}</span>
                                    </div>
                                </div>
                            )}
                            {activeRank.id < currentId && (
                                <div className="mt-5 w-full">
                                    <div className="h-2 w-full bg-emerald-500/80" />
                                    <div className="mt-1 text-[10px] uppercase tracking-widest text-emerald-400">Rank achieved</div>
                                </div>
                            )}
                            {activeRank.id === nextId && data.next && (
                                <div className="mt-5 w-full">
                                    <div className="label-eyebrow text-accent-red">Up next · sneak peek</div>
                                    <div className="mt-2 h-2 w-full bg-border">
                                        <div className="h-full bg-accent-red" style={{ width: `${data.pct}%` }} />
                                    </div>
                                    <div className="mt-1 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                                        <span>{data.into_current.toLocaleString()} earned in {data.current.name}</span>
                                        <span className="text-accent-red">{data.to_next.toLocaleString()} EXP to unlock</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Live rank-up celebration — same component used post-workout */}
            <RankUpModal
                rankUp={rankUpEvent?.rankUp}
                expEarned={rankUpEvent?.expEarned}
                totalExp={data?.total_exp}
                onClose={() => setRankUpEvent(null)}
            />
        </div>
    );
}
